import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_STYLES, kg } from "@/lib/calc";
import { productEmoji, shiftLabel, lineEmoji } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { exportShift } from "@/lib/excel";
import { EditReasonPicker, joinReason } from "@/components/EditReasonPicker";
import { toast } from "sonner";
import { Download, History, Pencil, Trash2, Plus, Wand2, ChevronLeft, ChevronRight, Search, Camera, Upload, Loader2, X, FileText, ShieldCheck } from "lucide-react";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function EditPhoto({ photo, onRemove }) {
  const [url, setUrl] = useState(null);
  const isPdf = (photo.storage_path || "").toLowerCase().endsWith(".pdf");
  useEffect(() => {
    let live = true; let obj = null;
    api.get(`/files/${photo.storage_path}`, { responseType: "blob" }).then((r) => { if (live) { obj = URL.createObjectURL(r.data); setUrl(obj); } }).catch(() => {});
    return () => { live = false; if (obj) URL.revokeObjectURL(obj); };
  }, [photo.storage_path]);
  const viewUrl = `${API_BASE}/api/files/view/${photo.id}`;
  return (
    <div className="relative">
      <button type="button" data-testid={`edit-photo-${photo.id}`} onClick={() => window.open(viewUrl, "_blank")} className="block w-20 h-20 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 active:scale-95 transition-transform">
        {isPdf ? <div className="w-full h-full flex flex-col items-center justify-center bg-rose-50 text-rose-500"><FileText size={20} /><span className="text-[9px] font-bold mt-0.5">PDF</span></div>
               : url ? <img src={url} alt="evidence" className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse" />}
      </button>
      {onRemove && <button type="button" data-testid={`edit-photo-remove-${photo.id}`} onClick={onRemove} className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full w-5 h-5 grid place-items-center shadow"><X size={12} strokeWidth={3} /></button>}
    </div>
  );
}

const TYPES = [{ v: "pool", l: "Pool (minutes)" }, { v: "buckets", l: "Buckets" }, { v: "direct", l: "Direct kg" }, { v: "piece", l: "Piece" }];

// Admin-only verification: tick when the weights & log for a shift are checked and balanced.
function ReviewBox({ e, reload }) {
  const [note, setNote] = useState(e.review_note || "");
  const [busy, setBusy] = useState(false);
  const save = async (reviewed, n) => {
    setBusy(true);
    try {
      await api.post(`/entries/${e.id}/review`, { reviewed, note: n || "" });
      await reload();
      if (reviewed) toast.success("Verified — checked & balanced ✓"); else toast.message("Review cleared");
    } catch { toast.error("Could not update review"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 border-t border-slate-100 pt-2" data-testid={`review-box-${e.id}`}>
      <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none">
        <input type="checkbox" data-testid={`review-check-${e.id}`} disabled={busy} checked={!!e.reviewed} onChange={(ev) => save(ev.target.checked, note)} className="w-4 h-4 accent-teal-600" />
        {e.reviewed
          ? <span className="text-teal-700 inline-flex items-center gap-1"><ShieldCheck size={15} strokeWidth={2.5} /> Checked &amp; balanced</span>
          : <span className="text-slate-600">Mark checked &amp; balanced</span>}
      </label>
      {e.reviewed && (
        <div className="mt-1.5 pl-6 space-y-1">
          <div className="text-[11px] text-slate-500 font-num">by {e.reviewed_by_name || "—"}{e.reviewed_at ? ` · ${new Date(e.reviewed_at).toLocaleString("en-AU")}` : ""}</div>
          <Input data-testid={`review-note-${e.id}`} value={note} onChange={(ev) => setNote(ev.target.value)} onBlur={() => { if ((note || "") !== (e.review_note || "")) save(true, note); }} placeholder="Add a note (optional)…" className="h-8 text-xs rounded-lg" />
        </div>
      )}
    </div>
  );
}

function ReviewFilter({ value, onChange }) {
  const opts = [["all", "All"], ["pending", "Pending review"], ["reviewed", "Reviewed"]];
  return (
    <div className="flex bg-slate-100 rounded-full p-1 text-xs font-semibold" data-testid="review-filter">
      {opts.map(([v, l]) => (
        <button key={v} data-testid={`review-filter-${v}`} onClick={() => onChange(v)} className={`px-3 py-1.5 rounded-full transition-colors ${value === v ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}>{l}</button>
      ))}
    </div>
  );
}

function StatusFilter({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="status-filter" className="h-9 w-44 rounded-full text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All statuses</SelectItem>
        <SelectItem value="attention">⚠ Needs attention</SelectItem>
        <SelectItem value="UNALLOCATED">Unbalanced kilos</SelectItem>
        <SelectItem value="NO PRODUCTS">No products</SelectItem>
        <SelectItem value="CHECK DATA">Check data</SelectItem>
        <SelectItem value="NO WORKERS">No workers</SelectItem>
      </SelectContent>
    </Select>
  );
}

const SHIFT_ORDER = ["night", "day", "afternoon", "pooraka_day"];
const SHIFT_SHORT = { night: "1st · Night", day: "2nd · Day", afternoon: "3rd · Afternoon", pooraka_day: "Pooraka" };
const shiftTallyOf = (list) => {
  const m = {}; SHIFT_ORDER.forEach((k) => (m[k] = { kg: 0, count: 0 }));
  list.forEach((e) => { const k = e.shift; if (!m[k]) m[k] = { kg: 0, count: 0 }; m[k].kg += Number(e.total_worked) || 0; m[k].count += 1; });
  return m;
};

// Filter chips by shift + live per-shift kilos so totals can be cross-checked.
function ShiftFilterBar({ tally, value, onChange }) {
  const total = SHIFT_ORDER.reduce((s, k) => s + (tally[k]?.kg || 0), 0);
  const pill = (on) => `px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`;
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="shift-filter">
      <button data-testid="shift-filter-all" onClick={() => onChange("all")} className={pill(value === "all")}>All shifts · <span className="font-num">{kg(total)}</span></button>
      {SHIFT_ORDER.map((k) => (
        <button key={k} data-testid={`shift-filter-${k}`} onClick={() => onChange(value === k ? "all" : k)} className={pill(value === k)}>
          {SHIFT_SHORT[k]} · <span className="font-num">{kg(tally[k]?.kg || 0)}</span>
        </button>
      ))}
    </div>
  );
}

export default function MyLog() {
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [edit, setEdit] = useState(null);       // original entry snapshot
  const [draft, setDraft] = useState(null);      // editable copy
  const [reason, setReason] = useState("");
  const [reasonTags, setReasonTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editUploading, setEditUploading] = useState(false);

  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const [scope, setScope] = useState("mine");
  const [delId, setDelId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [supWeek, setSupWeek] = useState(null);
  const [flatWeek, setFlatWeek] = useState(null);
  const [reviewFilter, setReviewFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [shiftFilter, setShiftFilter] = useState("all");
  const matchStatus = (e) => statusFilter === "all" || (statusFilter === "attention" ? ["UNALLOCATED", "NO PRODUCTS", "CHECK DATA", "NO WORKERS"].includes(e.status) : e.status === statusFilter);
  const [supFilter, setSupFilter] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const toggle = (key) => setExpanded((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const goWeek = (wk) => { setSupWeek(wk); setDayFilter("all"); setExpanded(new Set()); };
  const load = () => api.get("/entries", { params: (scope === "all" || scope === "supervisor") ? {} : { mine: true } }).then((r) => setEntries(r.data));
  const doDelete = async () => {
    setDeleting(true);
    try { await api.delete(`/entries/${delId}`); toast.success("Shift deleted"); setDelId(null); load(); }
    catch { toast.error("Delete failed — admin only"); }
    finally { setDeleting(false); }
  };
  useEffect(() => { load(); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active)));
    api.get("/workers").then((r) => setWorkers(r.data.filter((w) => w.active !== false).sort((a, b) => (a.number || 0) - (b.number || 0))));
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  const openEdit = (e) => {
    setEdit(e);
    setDraft({
      pool_kgs: e.pool_kgs ?? 0,
      workers: (e.workers || []).map((w) => ({ worker_id: w.worker_id, name: w.name, type: w.type, number: w.number ?? w.minutes ?? w.buckets ?? w.direct_kg ?? w.pieces ?? 0 })),
      allocations: (e.allocations || []).map((a) => ({ product_id: a.product_id, code: a.code, kg: a.kg, ...(a.reason ? { reason: a.reason } : {}) })),
      photos: (e.photos || []).map((p) => ({ ...p })),
      line: e.line, shift: e.shift, date: e.date,
      training: (e.training || []).map((t) => ({ ...t })),
    });
    setReason(""); setReasonTags([]);
  };

  const uploadEditPhoto = async (file) => {
    if (!file) return;
    setEditUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setDraft((d) => ({ ...d, photos: [...(d.photos || []), data] })); toast.success("Photo added — remember to save the edit"); }
    catch { toast.error("Upload failed — please try again"); }
    finally { setEditUploading(false); }
  };

  // Build a plain-English summary of what changed for the audit trail
  const diffSummary = () => {
    const parts = [];
    if (Number(draft.pool_kgs) !== Number(edit.pool_kgs)) parts.push(`pool ${edit.pool_kgs}→${draft.pool_kgs}kg`);
    if (draft.date !== edit.date) parts.push(`date ${edit.date}→${draft.date}`);
    if (draft.shift !== edit.shift) parts.push(`shift ${edit.shift}→${draft.shift}`);
    if (draft.line !== edit.line) parts.push(`line ${edit.line}→${draft.line}`);
    if ((draft.photos?.length || 0) !== (edit.photos?.length || 0)) parts.push(`photos ${edit.photos?.length || 0}→${draft.photos?.length || 0}`);
    draft.workers.forEach((w, i) => {
      const o = edit.workers[i] || {};
      const on = o.number ?? o.minutes ?? o.buckets ?? o.direct_kg ?? o.pieces ?? 0;
      if (w.type !== o.type) parts.push(`${w.name} method ${o.type}→${w.type}`);
      if (Number(w.number) !== Number(on)) parts.push(`${w.name} ${on}→${w.number}`);
    });
    draft.allocations.forEach((a, i) => {
      const o = edit.allocations[i] || {};
      if (a.code !== o.code) parts.push(`product ${o.code || "—"}→${a.code}`);
      if (Number(a.kg) !== Number(o.kg)) parts.push(`${a.code} ${o.kg}→${a.kg}kg`);
    });
    return parts.join("; ") || "no field values changed";
  };

  const setW = (i, p) => setDraft((d) => ({ ...d, workers: d.workers.map((w, idx) => (idx === i ? { ...w, ...p } : w)) }));
  const addW = () => setDraft((d) => ({ ...d, workers: [...d.workers, { worker_id: "", name: "", type: "pool", number: 0 }] }));
  const delW = (i) => setDraft((d) => ({ ...d, workers: d.workers.filter((_, idx) => idx !== i) }));
  const setA = (i, p) => setDraft((d) => ({ ...d, allocations: d.allocations.map((a, idx) => (idx === i ? { ...a, ...p } : a)) }));
  const addA = () => setDraft((d) => ({ ...d, allocations: [...d.allocations, { product_id: "", code: "", kg: "" }] }));
  const delA = (i) => setDraft((d) => ({ ...d, allocations: d.allocations.filter((_, idx) => idx !== i) }));

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const dbw = edit?.bucket_weight || 10;
  const draftHasPool = !!draft && draft.workers.some((w) => w.type === "pool");
  const dBucketDirect = draft ? draft.workers.reduce((s, w) => (w.type === "buckets" ? s + (Number(w.number) || 0) * dbw : w.type === "direct" ? s + (Number(w.number) || 0) : s), 0) : 0;
  const dWorked = round2(dBucketDirect + (Number(draft?.pool_kgs) || 0));
  const dAllocated = round2(draft ? draft.allocations.reduce((s, a) => s + (Number(a.kg) || 0), 0) : 0);
  const dRemaining = round2(dWorked - dAllocated);
  const dBalanced = dWorked > 0 && Math.abs(dRemaining) < 0.01;
  const autoBalance = () => {
    if (dWorked <= 0) return toast.error("Enter the workers' kilos / pool first");
    if (Math.abs(dRemaining) < 0.01) return toast.info("Already balanced ✓");
    const empties = draft.allocations.map((a, i) => ({ a, i })).filter(({ a }) => (a.product_id || (a.reason || "").trim()) && !(Number(a.kg) > 0));
    setDraft((d) => {
      const allocs = [...d.allocations];
      if (dRemaining > 0 && empties.length) {
        const each = round2(dRemaining / empties.length);
        empties.forEach(({ i }, idx) => { allocs[i] = { ...allocs[i], kg: idx === empties.length - 1 ? round2(dRemaining - each * (empties.length - 1)) : each }; });
      } else {
        const li = allocs.length - 1;
        if (li < 0) return d;
        allocs[li] = { ...allocs[li], kg: round2((Number(allocs[li].kg) || 0) + dRemaining) };
      }
      return { ...d, allocations: allocs };
    });
    toast.success("Balanced ✓ — worker kilos now match products");
  };

  const setT = (i, patch) => setDraft((d) => { const tr = [...(d.training || [])]; tr[i] = { ...tr[i], ...patch }; return { ...d, training: tr }; });
  const saveEdit = async () => {
    const fullReason = joinReason(reasonTags, reason);
    if (!fullReason) return toast.error("Tap a reason or type one for the audit trail");
    setSaving(true);
    try {
      const patch = {
        pool_kgs: Number(draft.pool_kgs) || 0,
        line: draft.line, shift: draft.shift, date: draft.date,
        workers: draft.workers.map((w) => ({ ...w, number: Number(w.number) || 0 })),
        allocations: draft.allocations.map((a) => ({ ...a, kg: Number(a.kg) || 0 })),
        photos: draft.photos || [],
        training: (draft.training || []).filter((t) => t.trainer || t.trainee || t.minutes).map((t) => ({ ...t, minutes: Number(t.minutes) || 0 })),
      };
      const summary = diffSummary();
      await api.put(`/entries/${edit.id}`, { field: "entry", old: `${edit.line} ${edit.shift}`, new: summary, reason: fullReason, patch });
      toast.success("Edit saved (audit logged)");
      setEdit(null); setDraft(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Edit failed"); }
    setSaving(false);
  };

  const num = (n) => (Number(n) || 0).toLocaleString("en-AU");

  const weeksAvailable = useMemo(() => [...new Set(entries.map((e) => e.week).filter((v) => v != null))].sort((a, b) => a - b), [entries]);
  useEffect(() => { if (scope === "supervisor" && supWeek == null && weeksAvailable.length) setSupWeek(weeksAvailable[weeksAvailable.length - 1]); }, [scope, weeksAvailable, supWeek]);
  useEffect(() => { if (scope !== "supervisor" && weeksAvailable.length && (flatWeek == null || !weeksAvailable.includes(flatWeek))) setFlatWeek(weeksAvailable[weeksAvailable.length - 1]); }, [scope, weeksAvailable, flatWeek]);
  const flatBase = useMemo(() => entries.filter((e) => e.week === flatWeek && (!isAdmin || reviewFilter === "all" || (reviewFilter === "pending" ? !e.reviewed : !!e.reviewed)) && (!isAdmin || matchStatus(e))), [entries, flatWeek, reviewFilter, statusFilter, isAdmin]);
  const flatTally = useMemo(() => shiftTallyOf(flatBase), [flatBase]);
  const flatEntries = useMemo(() => flatBase.filter((e) => shiftFilter === "all" || e.shift === shiftFilter), [flatBase, shiftFilter]);
  const flatKg = useMemo(() => flatEntries.reduce((a, e) => a + (Number(e.total_worked) || 0), 0), [flatEntries]);
  const weekEntries = useMemo(() => entries.filter((e) => e.week === supWeek), [entries, supWeek]);
  const weekDates = useMemo(() => {
    const ds = weekEntries.map((e) => e.date).filter(Boolean).sort();
    if (!ds.length) return [];
    const d = new Date(ds[0] + "T12:00:00"); d.setDate(d.getDate() - d.getDay());
    return Array.from({ length: 7 }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return x.toISOString().slice(0, 10); });
  }, [weekEntries]);
  const supervisors = useMemo(() => {
    const m = new Map(); entries.forEach((e) => { if (e.saved_by) m.set(e.saved_by, e.saved_by_name || "—"); });
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);
  const supGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (e) => !q || [e.saved_by_name, e.line, e.shift, ...(e.allocations || []).map((a) => a.code), ...(e.workers || []).map((w) => w.name)].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    const filtered = weekEntries.filter((e) => (supFilter === "all" || e.saved_by === supFilter) && (dayFilter === "all" || e.date === dayFilter) && (shiftFilter === "all" || e.shift === shiftFilter) && (reviewFilter === "all" || (reviewFilter === "pending" ? !e.reviewed : !!e.reviewed)) && matchStatus(e) && match(e));
    const m = new Map();
    filtered.forEach((e) => {
      if (!m.has(e.saved_by)) m.set(e.saved_by, { id: e.saved_by || "unknown", name: e.saved_by_name || "—", entries: [], totalKg: 0, count: 0 });
      const g = m.get(e.saved_by); g.entries.push(e); g.totalKg += Number(e.total_worked) || 0; g.count += 1;
    });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [weekEntries, supFilter, dayFilter, query, reviewFilter, statusFilter, shiftFilter]);
  const supTally = useMemo(() => shiftTallyOf(weekEntries), [weekEntries]);

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-3xl mx-auto">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-slate-800">{scope === "supervisor" ? "By Supervisor" : scope === "all" ? "All Shifts" : "My Log"}</h1>
          <p className="text-slate-500 text-sm">{scope === "supervisor" ? "Daily breakdown per supervisor — edit & cross-check" : scope === "all" ? "Every supervisor's filed shifts — admin edit" : "Your filed entries & edit trail"}</p>
        </div>
        {isAdmin && (
          <div className="flex bg-slate-100 rounded-full p-1 text-sm font-semibold shrink-0">
            <button data-testid="scope-mine" onClick={() => setScope("mine")} className={`px-3 py-1.5 rounded-full transition-colors ${scope === "mine" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}>Mine</button>
            <button data-testid="scope-all" onClick={() => setScope("all")} className={`px-3 py-1.5 rounded-full transition-colors ${scope === "all" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}>All shifts</button>
            <button data-testid="scope-supervisor" onClick={() => setScope("supervisor")} className={`px-3 py-1.5 rounded-full transition-colors ${scope === "supervisor" ? "bg-white text-teal-700 shadow-sm" : "text-slate-500"}`}>By supervisor</button>
          </div>
        )}
      </div>
      {scope === "supervisor" && (
        <div className="space-y-4" data-testid="supervisor-view">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1 bg-slate-50 rounded-full border border-slate-200">
                <button data-testid="sup-week-prev" onClick={() => goWeek(weeksAvailable[Math.max(0, weeksAvailable.indexOf(supWeek) - 1)] ?? supWeek)} className="p-2 text-teal-600"><ChevronLeft size={16} strokeWidth={2.5} /></button>
                <span className="font-num font-semibold text-slate-800 px-1 text-sm">Week {supWeek ?? "…"}</span>
                <button data-testid="sup-week-next" onClick={() => goWeek(weeksAvailable[Math.min(weeksAvailable.length - 1, weeksAvailable.indexOf(supWeek) + 1)] ?? supWeek)} className="p-2 text-teal-600"><ChevronRight size={16} strokeWidth={2.5} /></button>
              </div>
              <div className="relative flex-1 max-w-[260px]">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="sup-search" placeholder="Search name, line, product…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-8 h-9 rounded-full text-sm" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={supFilter} onValueChange={setSupFilter}>
                <SelectTrigger data-testid="sup-filter" className="h-9 w-48 rounded-full text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All supervisors</SelectItem>
                  {supervisors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={dayFilter} onValueChange={setDayFilter}>
                <SelectTrigger data-testid="day-filter" className="h-9 w-40 rounded-full text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All days</SelectItem>
                  {weekDates.map((d) => <SelectItem key={d} value={d}>{DOW[new Date(d + "T12:00:00").getDay()]} {d.slice(5)}</SelectItem>)}
                </SelectContent>
              </Select>
              <StatusFilter value={statusFilter} onChange={setStatusFilter} />
              <ReviewFilter value={reviewFilter} onChange={setReviewFilter} />
            </div>
            <ShiftFilterBar tally={supTally} value={shiftFilter} onChange={setShiftFilter} />
          </div>

          {supGroups.length === 0 && <div data-testid="sup-empty" className="text-slate-400 bg-white border border-slate-100 rounded-2xl p-8 text-center">No shifts match — try another week or clear the search.</div>}

          {supGroups.map((g) => (
            <div key={g.id} data-testid={`sup-panel-${g.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="font-heading font-bold text-slate-800">{g.name}</div>
                <div className="text-xs font-semibold text-slate-500">{g.count} shift{g.count === 1 ? "" : "s"} · <span className="font-num text-slate-800">{kg(g.totalKg)}</span></div>
              </div>
              <div className="grid grid-cols-7 gap-1 p-3">
                {weekDates.map((d) => {
                  const dayEntries = g.entries.filter((e) => e.date === d);
                  const dkg = dayEntries.reduce((s, e) => s + (Number(e.total_worked) || 0), 0);
                  const key = `${g.id}|${d}`;
                  const open = expanded.has(key);
                  const has = dayEntries.length > 0;
                  return (
                    <button key={d} data-testid={`sup-day-${g.id}-${d}`} disabled={!has} onClick={() => toggle(key)}
                      className={`rounded-xl border p-2 text-left transition-colors ${has ? (open ? "border-teal-400 bg-teal-50" : "border-teal-200 bg-teal-50/40 hover:bg-teal-50") : "border-slate-100 bg-slate-50 text-slate-300"}`}>
                      <div className="text-[10px] font-bold uppercase">{DOW[new Date(d + "T12:00:00").getDay()]}</div>
                      <div className="text-[10px] text-slate-400 font-num">{d.slice(5)}</div>
                      <div className="text-xs font-bold text-slate-700 mt-0.5">{has ? `${dayEntries.length}×` : "—"}</div>
                      {has && <div className="text-[10px] font-num text-slate-500">{kg(dkg)}</div>}
                    </button>
                  );
                })}
              </div>
              {weekDates.filter((d) => expanded.has(`${g.id}|${d}`)).map((d) => (
                <div key={d} className="px-3 pb-3 space-y-2">
                  <div className="text-xs font-bold text-slate-500 pt-1">{DOW[new Date(d + "T12:00:00").getDay()]} {d}</div>
                  {g.entries.filter((e) => e.date === d).map((e) => {
                    const s = STATUS_STYLES[e.status];
                    return (
                      <div key={e.id} data-testid={`sup-shift-${e.id}`} className={`rounded-xl border border-slate-100 border-l-4 ${s.border} p-3`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-800 text-sm truncate">{e.line} · {shiftLabel(e.shift)}</div>
                            <div className="text-[11px] text-slate-500 font-num">Logged {new Date(e.saved_at).toLocaleString("en-AU")}</div>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${s.bg} ${s.text}`}>{s.label}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-slate-500">
                          <span>Worked <b className="font-num text-slate-800">{kg(e.total_worked)}</b></span>
                          <span>Allocated <b className="font-num text-slate-800">{kg(e.total_allocated)}</b></span>
                          <span>{e.workers?.length || 0} workers</span>
                        </div>
                        {e.edits?.length > 0 && (
                          <div className="mt-2 text-[11px] text-slate-500"><History size={11} className="inline mr-1" />{e.edits.length} edit{e.edits.length === 1 ? "" : "s"} · last: "{e.edits[e.edits.length - 1].reason}" ({e.edits[e.edits.length - 1].editor})</div>
                        )}
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" variant="outline" data-testid={`sup-edit-${e.id}`} onClick={() => openEdit(e)} className="gap-1 h-8 text-xs"><Pencil size={13} strokeWidth={2.5} /> Edit</Button>
                          <Button size="sm" variant="outline" onClick={() => exportShift(e, false)} className="gap-1 h-8 text-xs"><Download size={13} strokeWidth={2.5} /> Export</Button>
                          <Button size="sm" variant="outline" data-testid={`sup-delete-${e.id}`} onClick={() => setDelId(e.id)} className="gap-1 h-8 text-xs border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 size={13} strokeWidth={2.5} /> Delete</Button>
                        </div>
                        {isAdmin && <ReviewBox e={e} reload={load} />}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {scope !== "supervisor" && entries.length === 0 && <div className="text-slate-400 bg-white border border-border rounded-md p-8 text-center">You haven't filed any entries yet.</div>}
      {scope !== "supervisor" && isAdmin && entries.length > 0 && <div className="flex justify-end gap-2 items-center flex-wrap" data-testid="flat-review-filter-wrap"><StatusFilter value={statusFilter} onChange={setStatusFilter} /><ReviewFilter value={reviewFilter} onChange={setReviewFilter} /></div>}
      {scope !== "supervisor" && weeksAvailable.length > 0 && (
        <div data-testid="flat-week-nav" className="flex items-center justify-between bg-white border border-slate-100 rounded-2xl px-3 py-2 sticky top-2 z-10 shadow-sm">
          <button data-testid="flat-week-prev" onClick={() => setFlatWeek((w) => weeksAvailable[Math.max(0, weeksAvailable.indexOf(w) - 1)] ?? w)} disabled={weeksAvailable.indexOf(flatWeek) <= 0} className="p-2 text-teal-600 disabled:text-slate-300"><ChevronLeft size={18} strokeWidth={2.5} /></button>
          <div className="text-center">
            <div data-testid="flat-week-label" className="font-heading font-bold text-slate-800 text-sm">Week {flatWeek ?? "…"}</div>
            <div className="text-[11px] text-slate-500 font-num">{flatEntries.length} shift{flatEntries.length === 1 ? "" : "s"} · {kg(flatKg)}</div>
          </div>
          <button data-testid="flat-week-next" onClick={() => setFlatWeek((w) => weeksAvailable[Math.min(weeksAvailable.length - 1, weeksAvailable.indexOf(w) + 1)] ?? w)} disabled={weeksAvailable.indexOf(flatWeek) >= weeksAvailable.length - 1} className="p-2 text-teal-600 disabled:text-slate-300"><ChevronRight size={18} strokeWidth={2.5} /></button>
        </div>
      )}
      {scope !== "supervisor" && weeksAvailable.length > 0 && (
        <div className="bg-white border border-slate-100 rounded-2xl px-3 py-2.5 shadow-sm" data-testid="flat-shift-filter">
          <div className="text-xs font-semibold text-slate-500 mb-2">Filter by shift — tap to see each shift's kilos for the week</div>
          <ShiftFilterBar tally={flatTally} value={shiftFilter} onChange={setShiftFilter} />
        </div>
      )}
      {scope !== "supervisor" && entries.length > 0 && flatEntries.length === 0 && <div className="text-slate-400 bg-white border border-slate-100 rounded-2xl p-8 text-center">No shifts in Week {flatWeek}. Use the arrows to change week.</div>}
      {scope !== "supervisor" && flatEntries.map((e) => {
        const s = STATUS_STYLES[e.status];
        return (
          <div key={e.id} data-testid={`mylog-${e.id}`} className={`bg-white rounded-2xl border border-slate-100 border-l-4 ${s.border} p-4`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-heading font-bold text-slate-800 truncate">{e.line} · <span className="font-medium">{shiftLabel(e.shift)}</span></div>
                <div className="text-xs text-slate-500 font-num">{e.date} · Week {e.week} · {scope === "all" ? `by ${e.saved_by_name || "—"} · ` : "filed "}{new Date(e.saved_at).toLocaleString("en-AU")}</div>
              </div>
              <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${s.bg} ${s.text}`}>{s.label}</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
              <span>Worked <b className="font-num text-slate-800">{kg(e.total_worked)}</b></span>
              <span>Allocated <b className="font-num text-slate-800">{kg(e.total_allocated)}</b></span>
              <span>{e.workers?.length || 0} workers</span>
            </div>
            {e.edits?.length > 0 && (
              <div className="mt-3 border-t border-border pt-2">
                <div className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1"><History size={13} /> EDIT TRAIL</div>
                {e.edits.map((ed, i) => (
                  <div key={i} className="text-xs text-slate-500 font-num">{ed.new} · <span className="italic">"{ed.reason}"</span> · {ed.editor} · {new Date(ed.at).toLocaleDateString("en-AU")}</div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" data-testid={`edit-${e.id}`} onClick={() => openEdit(e)} className="gap-1.5"><Pencil size={14} strokeWidth={2.5} /> Edit</Button>
              <Button size="sm" variant="outline" onClick={() => exportShift(e, false)} className="gap-1.5"><Download size={14} strokeWidth={2.5} /> Export</Button>
              {isAdmin && <Button size="sm" variant="outline" data-testid={`delete-${e.id}`} onClick={() => setDelId(e.id)} className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 size={14} strokeWidth={2.5} /> Delete</Button>}
            </div>
            {isAdmin && <ReviewBox e={e} reload={load} />}
          </div>
        );
      })}

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) { setEdit(null); setDraft(null); } }}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-slate-800">Edit entry — {edit?.line} {shiftLabel(edit?.shift)}</DialogTitle><DialogDescription className="text-xs">Correct workers, kilos or products for this filed shift.</DialogDescription></DialogHeader>
          {draft && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">Append-only. The original values are preserved for the 7-year Fair Work audit — nothing is ever deleted. Correct any field below, then give a reason.</p>

              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs font-semibold text-slate-600">Line</label>
                  <Select value={draft.line} onValueChange={(v) => setDraft({ ...draft, line: v })}>
                    <SelectTrigger data-testid="edit-line" className="h-11 mt-1 rounded-xl"><SelectValue placeholder="Line" /></SelectTrigger>
                    <SelectContent>{(meta?.lines?.length ? meta.lines : [draft.line].filter(Boolean)).map((l) => <SelectItem key={l} value={l}>{lineEmoji(l)} {l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs font-semibold text-slate-600">Shift</label>
                  <Select value={draft.shift} onValueChange={(v) => setDraft({ ...draft, shift: v })}>
                    <SelectTrigger data-testid="edit-shift" className="h-11 mt-1 rounded-xl"><SelectValue placeholder="Shift" /></SelectTrigger>
                    <SelectContent>{(meta?.shifts?.length ? meta.shifts : []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs font-semibold text-slate-600">Date</label><Input data-testid="edit-date" type="date" className="h-11 mt-1" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} /></div>
                <div><label className="text-xs font-semibold text-slate-600">Pool kilos</label><Input data-testid="edit-poolkgs" type="number" inputMode="decimal" className="h-11 mt-1 font-num" value={draft.pool_kgs} onChange={(e) => setDraft({ ...draft, pool_kgs: e.target.value })} />
                  {draftHasPool && <button data-testid="edit-pool-auto" onClick={() => setDraft((d) => ({ ...d, pool_kgs: round2(Math.max(0, dAllocated - dBucketDirect)) }))} className="text-[11px] font-semibold text-teal-700 mt-1">Auto from products ({kg(round2(Math.max(0, dAllocated - dBucketDirect)))})</button>}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-slate-600">Workers ({draft.workers.length})</div>
                  <div className="flex items-center gap-3">
                    {draft.workers.length > 1 && <button data-testid="edit-dt-apply-all" onClick={() => { const f = draft.workers[0]; if (!f) return; setDraft((d) => ({ ...d, workers: d.workers.map((w) => ({ ...w, downtime_min: f.downtime_min || "", downtime_reason: f.downtime_reason || "" })) })); toast.success("Downtime applied to all workers"); }} className="text-xs font-bold text-slate-500">Apply DT to all</button>}
                    <button data-testid="edit-add-worker" onClick={addW} className="text-xs font-bold text-teal-700 flex items-center gap-1"><Plus size={13} strokeWidth={3} /> Add worker</button>
                  </div>
                </div>
                <div className="space-y-2">
                  {draft.workers.map((w, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <Select value={w.worker_id || ""} onValueChange={(v) => { const wk = workers.find((x) => x.id === v); setW(i, { worker_id: v, name: wk ? `${wk.name}` : "" }); }}>
                        <SelectTrigger data-testid={`edit-worker-name-${i}`} className="h-10 flex-1 min-w-[8rem] rounded-xl text-xs"><SelectValue placeholder={w.name || "Select worker"} /></SelectTrigger>
                        <SelectContent>{workers.map((wk) => <SelectItem key={wk.id} value={wk.id}>#{wk.number} {wk.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={w.type} onValueChange={(v) => setW(i, { type: v })}>
                        <SelectTrigger data-testid={`edit-worker-type-${i}`} className="h-10 w-24 rounded-xl text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input data-testid={`edit-worker-num-${i}`} type="number" inputMode="decimal" className="h-10 w-24 font-num text-base rounded-xl" value={w.number} onChange={(e) => setW(i, { number: e.target.value })} />
                      <Input data-testid={`edit-worker-dt-${i}`} type="number" inputMode="decimal" placeholder="DT min" title="Downtime minutes" className="h-10 w-20 font-num text-xs rounded-xl" value={w.downtime_min || ""} onChange={(e) => setW(i, { downtime_min: e.target.value })} />
                      <Input data-testid={`edit-worker-dtr-${i}`} placeholder="DT reason" className="h-10 w-32 text-xs rounded-xl" value={w.downtime_reason || ""} onChange={(e) => setW(i, { downtime_reason: e.target.value })} />
                      <button data-testid={`edit-worker-del-${i}`} onClick={() => delW(i)} className="text-rose-400"><Trash2 size={16} strokeWidth={2.5} /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-slate-600">Training log</div>
                  <button data-testid="edit-add-training" onClick={() => setDraft((d) => ({ ...d, training: [...(d.training || []), { trainer: "", trainee: "", minutes: "", reason: "" }] }))} className="text-xs font-bold text-teal-700 flex items-center gap-1"><Plus size={13} strokeWidth={3} /> Add</button>
                </div>
                <div className="space-y-2">
                  {(draft.training || []).map((t, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <Select value={t.trainer || ""} onValueChange={(v) => setT(i, { trainer: v })}><SelectTrigger data-testid={`edit-train-trainer-${i}`} className="h-10 flex-1 min-w-[7rem] rounded-xl text-xs"><SelectValue placeholder="Trainer" /></SelectTrigger><SelectContent>{workers.map((wk) => <SelectItem key={wk.id} value={`#${wk.number} ${wk.name}`}>#{wk.number} {wk.name}</SelectItem>)}</SelectContent></Select>
                      <span className="text-xs text-slate-400">→</span>
                      <Select value={t.trainee || ""} onValueChange={(v) => setT(i, { trainee: v })}><SelectTrigger data-testid={`edit-train-trainee-${i}`} className="h-10 flex-1 min-w-[7rem] rounded-xl text-xs"><SelectValue placeholder="Trainee" /></SelectTrigger><SelectContent>{workers.map((wk) => <SelectItem key={wk.id} value={`#${wk.number} ${wk.name}`}>#{wk.number} {wk.name}</SelectItem>)}</SelectContent></Select>
                      <Input data-testid={`edit-train-min-${i}`} type="number" inputMode="decimal" placeholder="min" className="h-10 w-20 font-num text-xs rounded-xl" value={t.minutes || ""} onChange={(e) => setT(i, { minutes: e.target.value })} />
                      <Input data-testid={`edit-train-reason-${i}`} placeholder="reason" className="h-10 w-32 text-xs rounded-xl" value={t.reason || ""} onChange={(e) => setT(i, { reason: e.target.value })} />
                      <button data-testid={`edit-train-del-${i}`} onClick={() => setDraft((d) => ({ ...d, training: (d.training || []).filter((_, x) => x !== i) }))} className="text-rose-400"><Trash2 size={16} strokeWidth={2.5} /></button>
                    </div>
                  ))}
                  {(!draft.training || draft.training.length === 0) && <div className="text-xs text-slate-400">No training logged. Add a row if a worker trained another (recorded only, for manual pay).</div>}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-semibold text-slate-600">Product allocation</div>
                  <button data-testid="edit-add-alloc" onClick={addA} className="text-xs font-bold text-teal-700 flex items-center gap-1"><Plus size={13} strokeWidth={3} /> Add</button>
                </div>
                <div className="space-y-2">
                  {draft.allocations.map((a, i) => {
                    const prod = products.find((p) => p.id === a.product_id);
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xl">{prod ? productEmoji(prod) : a.reason != null ? "✏️" : "🍖"}</span>
                        {a.reason != null && !a.product_id ? <Input data-testid={`edit-alloc-reason-${i}`} value={a.reason} onChange={(e) => setA(i, { reason: e.target.value })} placeholder="Reason" className="h-10 flex-1 rounded-xl text-xs" /> :
                        <Select value={a.product_id} onValueChange={(v) => { if (v === "__reason") return setA(i, { product_id: "", code: "REASON", reason: "" }); const p = products.find((x) => x.id === v); setA(i, { product_id: v, code: p?.code || "", reason: undefined }); }}>
                          <SelectTrigger data-testid={`edit-alloc-product-${i}`} className="h-10 flex-1 rounded-xl text-xs"><SelectValue placeholder={a.code || "Product"} /></SelectTrigger>
                          <SelectContent><SelectItem value="__reason">✏️ Other — write a reason</SelectItem>{products.map((p) => <SelectItem key={p.id} value={p.id}>{productEmoji(p)} {p.code}</SelectItem>)}</SelectContent>
                        </Select>}
                        <Input data-testid={`edit-alloc-kg-${i}`} type="number" inputMode="decimal" className="h-10 w-24 font-num text-base rounded-xl" value={a.kg} onChange={(e) => setA(i, { kg: e.target.value })} />
                        <button data-testid={`edit-alloc-del-${i}`} onClick={() => delA(i)} className="text-rose-400"><Trash2 size={16} strokeWidth={2.5} /></button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={`rounded-xl border-2 p-3 ${dBalanced ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`}>
                <div className="flex items-center justify-between text-sm"><span className="font-semibold text-slate-600">Worker kilos (worked)</span><span className="font-num font-bold text-slate-800">{kg(dWorked)}</span></div>
                <div className="flex items-center justify-between text-sm mt-0.5"><span className="font-semibold text-slate-600">Product kilos (allocated)</span><span className="font-num font-bold text-slate-800">{kg(dAllocated)}</span></div>
                <div className="flex items-center justify-between mt-1.5">
                  <span data-testid="edit-balance" className={`text-sm font-bold ${dBalanced ? "text-green-700" : "text-red-600"}`}>{dBalanced ? "✓ Balanced" : `${kg(Math.abs(dRemaining))} kg ${dRemaining > 0 ? "unallocated" : "over"}`}</span>
                  {!dBalanced && <Button data-testid="edit-auto-balance" size="sm" onClick={autoBalance} className="btn-grad text-white rounded-full h-8 gap-1"><Wand2 size={14} strokeWidth={2.5} /> Auto-balance</Button>}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-600 mb-1.5">Photos / documents (evidence) — tap to view & rotate</div>
                {draft.photos && draft.photos.length > 0 ? (
                  <div className="flex flex-wrap gap-2" data-testid="edit-photos">
                    {draft.photos.map((p) => <EditPhoto key={p.id} photo={p} onRemove={() => setDraft((d) => ({ ...d, photos: d.photos.filter((x) => x.id !== p.id) }))} />)}
                  </div>
                ) : <div className="text-xs text-slate-400" data-testid="edit-no-photos">No photos on this shift yet.</div>}
                <div className="flex gap-2 mt-2">
                  <label data-testid="edit-photo-camera" className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-teal-300 py-2.5 text-teal-700 text-xs font-semibold ${editUploading ? "opacity-50 pointer-events-none" : "cursor-pointer active:scale-[0.98]"}`}>
                    <Camera size={15} strokeWidth={2.5} /> Take photo
                    <input type="file" accept="image/*" capture="environment" className="hidden" disabled={editUploading} onChange={(e) => { uploadEditPhoto(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                  <label data-testid="edit-photo-upload" className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-sky-300 py-2.5 text-sky-700 text-xs font-semibold ${editUploading ? "opacity-50 pointer-events-none" : "cursor-pointer active:scale-[0.98]"}`}>
                    <Upload size={15} strokeWidth={2.5} /> Upload photo / doc
                    <input type="file" accept="image/*,application/pdf" className="hidden" disabled={editUploading} onChange={(e) => { uploadEditPhoto(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                </div>
                {editUploading && <div data-testid="edit-upload-loading" className="flex items-center gap-2 text-xs text-teal-700 mt-2 font-semibold"><Loader2 className="animate-spin" size={14} /> Please wait while your photo / document is loading…</div>}
              </div>

              <EditReasonPicker tags={reasonTags} onTags={setReasonTags} text={reason} onText={setReason} />
            </div>
          )}
          <DialogFooter><Button data-testid="edit-save" disabled={saving} onClick={saveEdit} className="btn-grad text-white rounded-full">{saving ? "Saving…" : "Save edit (audit logged)"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader><DialogTitle className="text-rose-600 flex items-center gap-2"><Trash2 size={18} strokeWidth={2.5} /> Delete this shift?</DialogTitle><DialogDescription className="text-xs">This permanently removes the shift and its kilos. This cannot be undone — it is recorded in the admin audit log.</DialogDescription></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelId(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="delete-confirm" disabled={deleting} onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-full">{deleting ? "Deleting…" : "Delete shift"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
